import React, { useMemo } from 'react';
import { InfoBox, Spacing, SpacingName } from '@doveit/lego';
import { NextPage } from 'next';
import { highlightToHTMLString } from '@doveit/hammer';
import LayoutWrapper from '../client/layouts/layout-wrapper/LayoutWrapper';
import AppLayout from '../client/layouts/app-layout/AppLayout';
import PageLayout from '../client/layouts/page-layout/PageLayout';
import { getStructuredData, getBreadcrumbStructuredData } from '../data/structured';
import PageHead, { PageHeadProps } from '../client/components/page-head/PageHead';
import { getLatestSold } from '../services/properties/properties';
import { PropertySearchResult } from '../services/dtos/property';
import PropertyCard from '../client/components/property-card/PropertyCard';
import * as listStyle from '../client/components/properties-list/PropertiesList.style';
import * as styles from '../client/templates/serp/Serp.style';
import { getAdvByType } from '../services/advs/advs';
import { Adv } from '../domain/adv';
import { BreadcrumbItem } from '../client/components/breadcrumb/Breadcrumb';
import { AdvFluidType } from '../domain/graphcms';
import AdvFluid from '../client/components/adv-fluid/AdvFluid';
import { BANNER_SELL_PROPERTY } from '../client/themes/icons';
import { withError, withGetServerSideError } from '../client/hocs/with-server-side-error/withServerSideError';
import { importDynamicData } from '../services/importDynamicData';

export interface SoldPropertiesProps {
  SEO: PageHeadProps['SEO'],
  results: PropertySearchResult[],
  adv: Adv,
}

export const WARN_NO_RESULTS = 'Non abbiamo trovato immobili in linea con la tua ricerca.';

export const SoldProperties: NextPage<SoldPropertiesProps> = ({
  SEO,
  results,
  adv,
}) => {
  const breadcrumb = useMemo<BreadcrumbItem[]>(() => [{ label: 'Case vendute' }], []);

  return (
    <AppLayout activePage="sold-properties">
      <PageHead
        SEO={SEO}
        structuredData={getStructuredData(getBreadcrumbStructuredData(breadcrumb))}
      />

      <LayoutWrapper>
        <Spacing padding={[SpacingName.BIG, SpacingName.ZERO]}>
          <header css={styles.header}>
            <div>
              <h1
                css={styles.title}
                data-ref="sold-properties-title"
              >
                Case e appartamenti venduti
              </h1>
            </div>
          </header>

          {results.length > 0 && (
            <ul css={listStyle.list}>
              {results.map((property, index) => (
                <React.Fragment key={property.id}>
                  <li
                    css={listStyle.item}
                    data-ref="search-result"
                  >
                    <PropertyCard
                      title={property.title}
                      price={property.price}
                      suburb={property.suburb}
                      banner={property.soldLabel}
                      features={property.highlights.map(highlightToHTMLString)}
                      image={property.coverImageSet.medium}
                      description={property.abstract}
                      ariaLabel={property.title}
                    />
                  </li>

                  {(index === 2 && (
                    <li
                      data-ref="search-result-adv"
                    >
                      <div css={styles.itemAdv}>
                        <div css={styles.advWrapper}>
                          <AdvFluid
                            title={adv.title}
                            description={adv.description}
                            cta={adv.cta}
                            urlObject={adv.urlObject}
                            symbolPath={BANNER_SELL_PROPERTY}
                            dataRef="adv-fluid-v1-main-sell-properties-sold-serp"
                            dataTracking="adv-fluid-v1-main-sell-properties-sold-serp"
                          />
                        </div>
                      </div>
                    </li>
                  ))}
                </React.Fragment>
              ))}
            </ul>
          )}

          {results.length === 0 && (
            <div css={styles.warningMessage}>
              <InfoBox message={WARN_NO_RESULTS} />
            </div>
          )}
        </Spacing>
      </LayoutWrapper>

      <PageLayout.Wrapper>
        <PageLayout.Breadcrumb items={breadcrumb} />
      </PageLayout.Wrapper>
    </AppLayout>
  );
};

export const getServerSideProps = withGetServerSideError<SoldPropertiesProps>(async () => {
  const [data, adv, results] = await Promise.all([
    importDynamicData('pages/sold-properties.yml'),
    getAdvByType(AdvFluidType.SELL_PROPERTY),
    getLatestSold({}, { size: 15 }),
  ]);

  return {
    props: {
      SEO: {
        ...data.SEO,
        robots: ['noindex'],
      },
      adv,
      results,
    },
  };
});

export default withError(SoldProperties);
